import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@my-saas/ui";
import { Helmet } from "react-helmet-async";

import { useEngineStatus, usePlatformTenants, useTenantMutations, type PlatformTenant } from "@/client/services/platform";

const statusBadgeVariant: Record<PlatformTenant["status"], "primary" | "secondary" | "error"> = {
  ACTIVE: "primary",
  ONBOARDING: "secondary",
  SUSPENDED: "error",
};

export const PlatformTenantsPage = () => {
  const { tenants, loading } = usePlatformTenants();
  const { status: engine } = useEngineStatus();
  const { activate, suspend } = useTenantMutations();

  return (
    <>
      <Helmet>
        <title>Platform · Tenants</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Platform tenants</h1>
            <p className="text-sm text-muted-foreground">
              All tenants on this platform. Toggle status to load/unload them in the alert engine.
            </p>
          </div>
          {engine && (
            <div className="text-xs text-muted-foreground">
              Engine: <span className="font-mono">{engine.tenants.loaded}</span> loaded /{" "}
              <span className="font-mono">{tenants.length}</span> total
            </div>
          )}
        </div>

        {loading && <p className="text-sm text-muted-foreground">Loading tenants…</p>}

        {!loading && tenants.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>No tenants yet</CardTitle>
              <CardDescription>
                Tenants will appear here when users sign up and create them via the onboarding flow.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {tenants.map((t) => (
          <Card key={t.id}>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-3">
                  {t.name}
                  <Badge variant={statusBadgeVariant[t.status]}>{t.status}</Badge>
                  {t.runtimeLoaded && (
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-900">
                      runtime ✓
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="font-mono text-xs">
                  id: {t.id} · slug: {t.slug}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {t.status !== "ACTIVE" && (
                  <Button size="sm" variant="primary" onClick={() => activate.mutate(t.id)} disabled={activate.isPending}>
                    Activate
                  </Button>
                )}
                {t.status === "ACTIVE" && (
                  <Button size="sm" variant="error" onClick={() => suspend.mutate(t.id)} disabled={suspend.isPending}>
                    Suspend
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                <div>
                  <div className="text-muted-foreground">Members</div>
                  <div className="font-mono text-lg">{t.memberCount}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Created</div>
                  <div className="text-xs">{new Date(t.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Updated</div>
                  <div className="text-xs">{new Date(t.updatedAt).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">In runtime</div>
                  <div className="text-xs">{t.runtimeLoaded ? "Yes" : "No"}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};
